import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { PortalDsButton, PortalDsNoticeBar, portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import { Skeleton } from 'src/components/ui/skeleton';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'src/components/ui/sheet';
import {
  useCreateChatSessionMutation,
  useGetChatSessionQuery,
  useSendChatMessageMutation,
} from 'src/redux/survey/focus-group.endpoints';
import type { CombinedQuestionStats } from 'src/pages/view-survey/view-survey.types';
import type { FocusGroupMessage, SurveySegment } from 'src/types/focus-group.types';
import {
  formatSegmentShare,
  isDailyCapError,
  isTurnCapError,
  questionIndexForId,
  SIMULATED_RESPONDENT_LABEL,
} from './focus-group-utils';

const INITIAL_VISIBLE_MESSAGES = 40;

interface FocusGroupChatProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  segment: SurveySegment | null;
  totalAiRespondents: number;
  questions: CombinedQuestionStats[];
  searchParams: URLSearchParams;
}

function GroundingIndicator({
  message,
  questions,
  searchParams,
}: {
  message: FocusGroupMessage;
  questions: CombinedQuestionStats[];
  searchParams: URLSearchParams;
}) {
  const grounding = message.grounding;
  if (!grounding || message.role !== 'persona') {
    return null;
  }

  const baseClass = 'mt-2 inline-flex flex-wrap items-center gap-2 text-xs';
  if (grounding.evidence === 'extrapolation') {
    return (
      <div className={`${baseClass} rounded-md border border-dashed border-[#FDE68A] bg-[#FFFBEB] px-2 py-1 text-[#D97706]`}>
        <span className="font-medium">
          Extrapolation
        </span>
        <span>
          — inference beyond direct survey evidence
        </span>
      </div>
    );
  }

  if (grounding.evidence === 'persona') {
    return (
      <div className={`${baseClass} text-[#2563EB]`}>
        <span className="rounded bg-[#EFF6FF] px-2 py-0.5 font-medium">
          Persona attributes
        </span>
      </div>
    );
  }

  const questionLinks = grounding.questionIds
    .map((questionId) => {
      const index = questionIndexForId(questionId, questions);
      if (index == null) {
        return null;
      }
      const next = new URLSearchParams(searchParams);
      next.set('question', String(index));
      return {
        index,
        href: `?${next.toString()}`,
      };
    })
    .filter((item): item is { index: number; href: string } => item != null);

  return (
    <div className={`${baseClass} text-[#059669]`}>
      <span className="rounded bg-[#ECFDF5] px-2 py-0.5 font-medium">
        Survey answers
      </span>
      {questionLinks.length > 0 ? (
        <span className="inline-flex flex-wrap gap-2">
          {questionLinks.map((link) => (
            <Link
              key={link.index}
              to={link.href}
              className="underline decoration-[#A7F3D0] underline-offset-2"
            >
              Q
              {link.index + 1}
            </Link>
          ))}
        </span>
      ) : null}
    </div>
  );
}

function ChatMessage({
  message,
  questions,
  searchParams,
}: {
  message: FocusGroupMessage;
  questions: CombinedQuestionStats[];
  searchParams: URLSearchParams;
}) {
  const isUser = message.role === 'user';
  const outOfScope = message.role === 'persona' && !message.inScope;

  if (outOfScope) {
    return (
      <div
        className="rounded-xl border border-[#EDEEF2] bg-[#F7F8FA] px-4 py-3 text-[13.5px] text-[#5E6278]"
        data-testid="out-of-scope-message"
      >
        <p>
          {message.content}
        </p>
        <p className="mt-2 text-[11px] text-[#9CA3AF]">
          This question falls outside what the survey covered.
        </p>
      </div>
    );
  }

  return (
    <div
      className={`rounded-xl px-4 py-3 text-[13.5px] ${
        isUser
          ? 'ml-8 bg-[#2563EB] text-white'
          : 'mr-8 border border-[#EDEEF2] bg-white text-[#0D0F1A]'
      }`}
      data-testid={isUser ? 'user-message' : 'persona-message'}
    >
      <p>
        {message.content}
      </p>
      <GroundingIndicator
        message={message}
        questions={questions}
        searchParams={searchParams}
      />
    </div>
  );
}

export function FocusGroupChat({
  open,
  onOpenChange,
  segment,
  totalAiRespondents,
  questions,
  searchParams,
}: FocusGroupChatProps) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [localMessages, setLocalMessages] = useState<FocusGroupMessage[]>([]);
  const [suggestedOpeners, setSuggestedOpeners] = useState<string[]>([]);
  const [systemNotice, setSystemNotice] = useState<string | null>(null);
  const [failedDraft, setFailedDraft] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_MESSAGES);
  const [isSendingTurn, setIsSendingTurn] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  const [createSession, { isLoading: isCreatingSession }] = useCreateChatSessionMutation();
  const {
    data: sessionDetail,
    isLoading: isLoadingSession,
  } = useGetChatSessionQuery(
    { session_id: sessionId ?? '' },
    { skip: !sessionId }
  );
  const [sendMessage, { isLoading: isSending }] = useSendChatMessageMutation();

  useEffect(() => {
    if (!open || !segment) {
      return;
    }

    let cancelled = false;
    const startSession = async() => {
      setSessionId(null);
      setLocalMessages([]);
      setSuggestedOpeners([]);
      setSystemNotice(null);
      setFailedDraft(null);
      setVisibleCount(INITIAL_VISIBLE_MESSAGES);

      try {
        const session = await createSession({ segment_id: segment.id }).unwrap();
        if (cancelled) {
          return;
        }
        setSessionId(session.id);
        setSuggestedOpeners(session.suggested_openers ?? []);
      } catch {
        if (!cancelled) {
          setSystemNotice('Could not start a focus group session. Please try again.');
        }
      }
    };

    void startSession();
    return () => {
      cancelled = true;
    };
  }, [open, segment, createSession]);

  useEffect(() => {
    if (sessionDetail?.messages) {
      setLocalMessages(sessionDetail.messages);
    }
  }, [sessionDetail]);

  const displayedMessages = useMemo(() => {
    if (localMessages.length <= visibleCount) {
      return localMessages;
    }
    return localMessages.slice(localMessages.length - visibleCount);
  }, [localMessages, visibleCount]);

  const hiddenCount = Math.max(0, localMessages.length - visibleCount);

  const handleSend = async(content: string) => {
    if (!sessionId || !content.trim() || isSending || isSendingTurn) {
      return;
    }

    const trimmed = content.trim();
    setFailedDraft(null);
    setSystemNotice(null);
    setIsSendingTurn(true);

    const optimisticUserMessage: FocusGroupMessage = {
      id: `local-user-${Date.now()}`,
      role: 'user',
      content: trimmed,
      inScope: true,
    };
    setLocalMessages((messages) => [...messages, optimisticUserMessage]);

    try {
      const reply = await sendMessage({ session_id: sessionId, content: trimmed }).unwrap();
      setDraft('');
      setLocalMessages((messages) => [...messages, reply]);
    } catch (error) {
      const message = error && typeof error === 'object' && 'message' in error
        ? String((error as { message?: string }).message ?? '')
        : 'Message failed to send.';
      if (isTurnCapError(message) || isDailyCapError(message)) {
        setSystemNotice(message);
        setDraft(trimmed);
      } else {
        setFailedDraft(trimmed);
        setDraft(trimmed);
        setSystemNotice('Message failed to send. Your draft is preserved below.');
      }
      setLocalMessages((messages) => messages.filter((item) => item.id !== optimisticUserMessage.id));
    } finally {
      setIsSendingTurn(false);
    }
  };

  const personaName = segment?.persona?.name ?? segment?.label ?? 'Segment persona';
  const isTurnInFlight = isSending || isSendingTurn;
  const isComposerDisabled = isCreatingSession || isTurnInFlight || isLoadingSession || Boolean(systemNotice && (isTurnCapError(systemNotice) || isDailyCapError(systemNotice)));

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
    >
      <SheetContent className="flex w-full flex-col overflow-hidden p-0 sm:max-w-xl">
        <SheetHeader className={cn(portalDs.sheetHeader, 'space-y-0 text-left')}>
          <SheetTitle className={portalDs.sheetTitle}>
            {personaName}
          </SheetTitle>
          <SheetDescription className={portalDs.sheetDescription}>
            {segment ? formatSegmentShare(segment.size, totalAiRespondents) : null}
          </SheetDescription>
          <div
            className="mt-3"
            data-testid="simulated-respondent-label"
            role="note"
          >
            <PortalDsNoticeBar>
              {SIMULATED_RESPONDENT_LABEL}
            </PortalDsNoticeBar>
          </div>
        </SheetHeader>

        <div
          ref={listRef}
          className="flex-1 space-y-3 overflow-y-auto px-6 py-4"
          aria-live="polite"
          aria-relevant="additions"
          data-testid="focus-group-message-list"
        >
          {(isCreatingSession || isLoadingSession) && localMessages.length === 0 ? (
            <div className="space-y-3">
              <Skeleton className="h-16 w-4/5" />
              <Skeleton className="h-12 w-3/5" />
            </div>
          ) : null}

          {hiddenCount > 0 ? (
            <PortalDsButton
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setVisibleCount((count) => count + INITIAL_VISIBLE_MESSAGES)}
            >
              Show earlier messages (
              {hiddenCount}
              )
            </PortalDsButton>
          ) : null}

          {displayedMessages.map((message) => (
            <ChatMessage
              key={message.id}
              message={message}
              questions={questions}
              searchParams={searchParams}
            />
          ))}

          {isTurnInFlight ? (
            <div
              className={cn(portalDs.bodySm, 'text-[#5E6278]')}
              data-testid="typing-indicator"
            >
              {personaName}
              {' '}
              is responding…
            </div>
          ) : null}

          {systemNotice ? (
            <div
              className={cn(portalDs.alertBase, portalDs.alertDanger, 'px-3 py-2')}
              data-testid="focus-group-system-notice"
              role="status"
            >
              {systemNotice}
            </div>
          ) : null}
        </div>

        {suggestedOpeners.length > 0 && localMessages.length === 0 ? (
          <div className="border-t border-[#EDEEF2] px-6 py-3">
            <p className={cn(portalDs.sectionLabel, 'mb-2')}>
              Suggested openers
            </p>
            <div className="flex flex-wrap gap-2">
              {suggestedOpeners.map((opener) => (
                <PortalDsButton
                  key={opener}
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={isComposerDisabled}
                  onClick={() => { void handleSend(opener); }}
                >
                  {opener}
                </PortalDsButton>
              ))}
            </div>
          </div>
        ) : null}

        <form
          className="border-t border-[#EDEEF2] px-6 py-4"
          onSubmit={(event) => {
            event.preventDefault();
            void handleSend(draft);
          }}
        >
          {failedDraft ? (
            <div className={cn(portalDs.alertBase, portalDs.alertWarning, 'mb-2 items-center justify-between px-3 py-2')}>
              <span>
                Draft preserved after a failed send.
              </span>
              <PortalDsButton
                type="button"
                size="sm"
                variant="secondary"
                onClick={() => {
                  setDraft(failedDraft);
                  setFailedDraft(null);
                }}
              >
                Restore draft
              </PortalDsButton>
            </div>
          ) : null}
          <label
            className="sr-only"
            htmlFor="focus-group-message-input"
          >
            Message
          </label>
          <textarea
            id="focus-group-message-input"
            className={cn(portalDs.input, 'min-h-[88px] h-auto py-2.5')}
            value={draft}
            disabled={isComposerDisabled}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Ask this segment about the survey topic…"
          />
          <div className="mt-2 flex justify-end">
            <PortalDsButton
              type="submit"
              variant="accent"
              disabled={isComposerDisabled || !draft.trim()}
            >
              Send
            </PortalDsButton>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
